/**
 * @file This file contains the Settlement class, which represents an individual
 * settlement building or feature on the game grid with its own state.
 * @module Settlement
 */

/**
 * Represents an individual settlement instance on the grid.
 * It holds the state for a building, such as its inventory and cooldowns.
 *
 * @export
 * @class Settlement
 * @typedef {Settlement}
 */
export class Settlement {
    /**
     * @param {object} config - The base configuration for this settlement type from masterItemsConfig.
     * @param {{r: number, c: number}} position - The grid position of this settlement.
     */
    constructor(config, position) {
        // --- Static Properties from Config ---
        this.key = config.key;
        this.name = config.name;
        this.display = config.display;
        this.style = config.style;
        this.recipes = config.recipes || [];
        this.isStationary = true;
        this.idleIntervalInTicks = config.idleIntervalInTicks || 0;
        this.isUniversal = config.isUniversal || false;
        this.isBank = config.isBank || false;
        this.acceptsNoActionItems = config.acceptsNoActionItems || false;

        // --- Instance-specific State ---
        /** @type {{r: number, c: number}} */
        this.position = position;
        /** @type {Object.<string, number>} */
        this.imports = {};
        /** @type {Object.<string, number>} */
        this.exports = {};
        this.importsPaused = false;
        this.exportsPaused = false;

        // Universal warehouses use the 'exports' bucket for general storage.
        if (this.isUniversal) {
            this.inventory = this.exports;
        }

        /**
         * The exports object from the recipe currently in production. Null if not producing.
         * @type {Object.<string, number>|null}
         */
        this.productionOutput = null;
        /**
         * Cooldown in turns before this settlement can be used again.
         * @type {number}
         */
        this.cooldown = 0;
    }

    /**
     * Updates the settlement's state, primarily for handling production cooldowns.
     * @returns {Object.<string, number>|null} The exports object of the recipe that just finished, or null.
     */
    update() {
        if (this.cooldown > 0) {
            this.cooldown--;
            if (this.cooldown === 0 && this.productionOutput) {
                const finishedOutput = this.productionOutput;
                this.productionOutput = null;
                return finishedOutput; // Signal that production is complete
            }
        }
        return null;
    }

    /**
     * Checks if the settlement is currently on cooldown.
     * @returns {boolean} True if on cooldown.
     */
    isOnCooldown() {
        return this.cooldown > 0;
    }

    /**
     * Starts the production process for a given item.
     * @param {Object.<string, number>} output - The exports object from the recipe.
     */
    startProduction(output) {
        this.productionOutput = output;
        this.cooldown = this.idleIntervalInTicks;
        // Note: Ingredients should be consumed by the manager before calling this.
    }

    /**
     * Adds an item to the settlement's import stock.
     * @param {string} itemKey - The key of the item to add.
     * @param {number} quantity - The amount to add.
     */
    addToImports(itemKey, quantity) {
        this.imports[itemKey] = (this.imports[itemKey] || 0) + quantity;
    }

    /**
     * Adds an item to the settlement's export stock.
     * @param {string} itemKey - The key of the item to add.
     * @param {number} quantity - The amount to add.
     */
    addToExports(itemKey, quantity) {
        this.exports[itemKey] = (this.exports[itemKey] || 0) + quantity;
    }

    /**
     * Removes an item from the settlement's import stock.
     * @param {string} itemKey - The key of the item to remove.
     * @param {number} quantity - The amount to remove.
     * @returns {boolean} True if the items were successfully removed, false otherwise.
     */
    removeFromImports(itemKey, quantity) {
        if (this.imports[itemKey] && this.imports[itemKey] >= quantity) {
            this.imports[itemKey] -= quantity;
            if (this.imports[itemKey] === 0) {
                delete this.imports[itemKey];
            }
            return true;
        }
        return false;
    }

    /**
     * Removes an item from the settlement's export stock.
     * @param {string} itemKey - The key of the item to remove.
     * @param {number} quantity - The amount to remove.
     * @returns {boolean} True if the items were successfully removed, false otherwise.
     */
    removeFromExports(itemKey, quantity) {
        if (this.exports[itemKey] && this.exports[itemKey] >= quantity) {
            this.exports[itemKey] -= quantity;
            if (this.exports[itemKey] === 0) {
                delete this.exports[itemKey];
            }
            return true;
        }
        return false;
    }

    /**
     * Checks if the inventory contains all required items for a recipe.
     * @param {Object.<string, number>} requiredItems - The items required by a recipe.
     * @returns {boolean} True if all requirements are met.
     */
    hasIngredients(requiredItems) {
        // If there are no required items, the condition is met.
        if (!requiredItems || Object.keys(requiredItems).length === 0) {
            return true;
        }

        return Object.keys(requiredItems).every(key => (this.imports[key] || 0) >= requiredItems[key]);
    }

    /**
     * Gets the current state of the settlement for saving.
     * @returns {object} A serializable state object.
     */
    getState() {
        return {
            key: this.key,
            pos: this.position,
            imports: this.imports,
            exports: this.exports,
            cooldown: this.cooldown,
            productionOutput: this.productionOutput,
            importsPaused: this.importsPaused,
            exportsPaused: this.exportsPaused
        };
    }
}